import { selectIsCampaignPerformanceAlertsFeatureFlagOn } from 'core/userContext';
import { AlertStatus } from 'modules/campaigns/alerts/constants';
import { useProjectCampaignAlertsQuery } from 'modules/campaigns/alerts/hooks';
import { useQueryCampaignAlerts } from 'modules/campaigns/editor/hooks';
import { getAlertDetails, getAlertsToDisplay } from 'modules/campaigns/lib/utils/alertUtils';
import { CampaignTypeFilter, MessageMedium } from 'modules/domains/campaign';
import { ReactNode, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCampaign } from '../BaseSendEditor.hooks';
import {
  CampaignAlertJourneyContext,
  CampaignAlertJourneyContextState,
} from './CampaignAlertJourneyContext';

type Props = { children: ReactNode; messageMedium: MessageMedium };

export const CampaignAlertJourneyProvider = ({ children, messageMedium }: Props) => {
  const { campaign } = useCampaign();
  const { name, id: campaignId } = campaign;
  const campaignType = CampaignTypeFilter.Triggered;

  const isCampaignPerformanceAlertsEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isJourneysCampaignPerformanceAlertsEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );

  /** Common alert queries */

  const campaignAlertsQueryOptions = useMemo(
    () => ({
      skip:
        !campaignId ||
        !isCampaignPerformanceAlertsEnabled ||
        !isJourneysCampaignPerformanceAlertsEnabled,
      variables: { campaignId },
    }),
    [campaignId, isCampaignPerformanceAlertsEnabled, isJourneysCampaignPerformanceAlertsEnabled],
  );

  const {
    isLoading: isLoadingAlerts,
    refetch: refetchCampaignAlerts,
    results: campaignAlerts,
  } = useQueryCampaignAlerts(campaignAlertsQueryOptions);

  const projectAlertsQueryOptions = useMemo(
    () => ({
      skip: !isCampaignPerformanceAlertsEnabled || !isJourneysCampaignPerformanceAlertsEnabled,
      variables: {
        campaignType,
        messageMedium,
        status: AlertStatus.Enabled,
      },
    }),
    [isCampaignPerformanceAlertsEnabled, isJourneysCampaignPerformanceAlertsEnabled, messageMedium],
  );

  const { results: projectCampaignAlerts } =
    useProjectCampaignAlertsQuery(projectAlertsQueryOptions);

  const hasProjectAlerts = projectCampaignAlerts[messageMedium]?.length > 0;
  const alerts = getAlertsToDisplay(campaignAlerts, projectCampaignAlerts[messageMedium]);
  const enabledAlerts = alerts?.filter((alert) => alert.status === AlertStatus.Enabled);
  const alertDetails = getAlertDetails(enabledAlerts, campaignType);

  /** Alert takeover */

  const [isAlertTakeoverOpen, setIsAlertTakeoverOpen] = useState(false);

  const value: CampaignAlertJourneyContextState = {
    alertDetails,
    hasProjectAlerts,
    isLoadingAlerts,
    refetchCampaignAlerts,

    // takeover related
    campaignData: {
      campaignId: campaignId || 0,
      name: name || '',
      messageMedium,
      campaignType,
    },
    isAlertTakeoverOpen,
    onTakeoverOpen: setIsAlertTakeoverOpen,
  };

  return (
    <CampaignAlertJourneyContext.Provider value={value}>
      {children}
    </CampaignAlertJourneyContext.Provider>
  );
};
